import axios from "axios";
import { throttle } from "lodash";

import { isAxiosError, nukeAuthHeaderAxiosReqTransform, uuidv4 } from "../components/utils/util";

import type { LocalDbRecordingsData } from "./localdb";
import { arrayBufferToBlob, recordingsDB } from "./localdb";
import logger from "./logger";

const UPLOAD_PROGRESS_DEBOUNCE = 5000; // 5 seconds

type ThenArg<T> = T extends PromiseLike<infer U> ? U : T;
type IDB = ThenArg<ReturnType<typeof recordingsDB>>;
type InitialLocalDbRecordingsData = Pick<
  LocalDbRecordingsData,
  "streamId" | "storyId" | "sessionId" | "localTimeOffset" | "userId"
>;
type LoggerTags = { [prop: string]: string };

interface S3PresignedPost {
  url: string;
  fields: { [key: string]: string };
}

class LocalMediaStorageContext {
  idb: IDB = null;
  loggerTags: LoggerTags = {};

  async ensureIDB() {
    if (!this.idb) {
      this.idb = await recordingsDB();
    }
  }

  trace(msg: string, storyId?: string) {
    logger.info(logger.headers.SESSION_TRACE, { msg, ...this.loggerTags, ...(storyId ? { storyId } : {}) });
    // console.log(msg);
  }

  setLoggerTags(tags: LoggerTags) {
    this.loggerTags = tags;
  }
}

export class LocalAudioStream {
  ctx: LocalMediaStorageContext;
  id: string;
  streamId: string;
  storyId: string;
  sessionId: string;
  localTimeOffset: number;
  userId: string;
  recordingStartTime: number;
  recordingStopTime: number;
  type: string;
  uploadProgressCallback: UploadProgressCB;

  constructor(
    ctx: LocalMediaStorageContext,
    id: string,
    streamData: LocalDbRecordingsData,
    uploadCb: UploadProgressCB
  ) {
    this.ctx = ctx;
    this.id = id;
    this.streamId = streamData.streamId;
    this.storyId = streamData.storyId;
    this.sessionId = streamData.sessionId;
    this.localTimeOffset = streamData.localTimeOffset;
    this.userId = streamData.userId;
    this.recordingStartTime = streamData.recordingStartTime;
    this.recordingStopTime = streamData.recordingStopTime;
    this.type = streamData.type;
    this.uploadProgressCallback = uploadCb;
  }

  getDataBlob(): Blob {
    throw new Error("Deriving class MUST override getDataBlob()!");
  }

  async getUploadUrl(): Promise<S3PresignedPost> {
    try {
      const result = await axios.get(`/stories/${this.storyId}/media/upload-url`, {
        params: { id: this.id, streamId: this.streamId, mimeType: this.type },
      });
      return result.data.url as S3PresignedPost;
    } catch (err) {
      if (isAxiosError(err)) {
        if (err.response.status === 404) {
          // We have a special case if a story doesn't exist. In this case, there's no need
          // to upload the local recording and we simply move on to delete it.
          // (we also can't upload it anywhere as it would have no home :( )
          return null;
        }
      }
      throw err;
    }
  }

  async finish(): Promise<void> {
    const uploadUrl = await this.getUploadUrl();

    if (uploadUrl == null) {
      this.ctx.trace(`Story (${this.storyId}) was deleted; local recording NOT uploaded`, this.storyId);
      return;
    }

    const dataBlob = this.getDataBlob();

    const instance = axios.create({ baseURL: null, headers: undefined });
    const formData = new FormData();
    formData.append("x-amz-meta-recordingstart", this.recordingStartTime.toString());
    formData.append("x-amz-meta-recordingstop", this.recordingStopTime.toString());
    formData.append("x-amz-meta-localtimeoffset", this.localTimeOffset.toString());
    Object.keys(uploadUrl.fields).forEach((key) => formData.append(key, uploadUrl.fields[key]));
    formData.append("file", dataBlob);

    this.ctx.trace(
      `UPLOADING LOCAL RECORDING FILE(${this.id}) ${uploadUrl.url}; Key=${uploadUrl.fields.key}`,
      this.storyId
    );

    const throttledProgressCallback = throttle(
      (evt) => this.uploadProgressCallback(this, evt),
      UPLOAD_PROGRESS_DEBOUNCE,
      {
        leading: true,
      }
    );

    await instance.post(uploadUrl.url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      transformRequest: nukeAuthHeaderAxiosReqTransform,
      onUploadProgress: (evt: ProgressEvent) => {
        throttledProgressCallback({
          totalBytes: dataBlob.size,
          // apparently evt.loaded isn't bytes, it's a unitless ratio.
          // Anecdotally, this appears to be ~15-20% off from the real value,
          // so it's probably in something like KiB instead of KB
          // https://developer.mozilla.org/en-US/docs/Web/API/ProgressEvent/loaded
          // The docs have this to say:
          //  A 64-bit unsigned integer value indicating the amount of work
          //  already performed by the underlying process. The ratio of work done
          //  can be calculated by dividing total by the value of this property.
          //  When downloading a resource using HTTP, this only counts the body of
          // the HTTP message, and doesn't include headers and other overhead.
          bytesUploaded: (evt.loaded / evt.total) * dataBlob.size,
        });
      },
    });
  }

  async delete(): Promise<void> {
    const tx = this.ctx.idb.transaction("recordings", "readwrite");
    const store = tx.objectStore("recordings");
    this.ctx.trace(`Deleting ${this.id} from indexedDB`, this.storyId);
    await store.delete(this.id);
    await tx.done;

    // Our sync implementation below pulls one doc at a time and if the doc isn't ready for
    // upload (i.e. it is presently being written to) it will stop. Once we delete a doc
    // we want to invoke a new sync in case there's anything else in the IDB that we should
    // upload
    void _LMS.syncRecordings(true);
  }
}

export class InMemoryLocalAudioStream extends LocalAudioStream {
  localRecordedBlobs: Blob[];

  constructor(ctx: LocalMediaStorageContext, params: InitialLocalDbRecordingsData, uploadCb: UploadProgressCB) {
    const now = Date.now();
    super(
      ctx,
      uuidv4(),
      {
        recordingStartTime: now,
        recordingStopTime: null,
        recordedArrayBuffer: null,
        type: "audio/webm",
        ...params,
      },
      uploadCb
    );
    this.localRecordedBlobs = [];
  }

  getDataBlob(): Blob {
    return new Blob(this.localRecordedBlobs, { type: this.type });
  }

  async pushChunk(event: BlobEvent): Promise<void> {
    if (!event.data || !event.data.size) {
      return;
    }
    // TODO: Review - Are we writing out the same data over and over here? Is that an issue?
    this.localRecordedBlobs.push(event.data);
    this.type = event.data.type || "audio/webm";
    this.recordingStopTime = Date.now();

    const arrayBuffer = await new Blob(this.localRecordedBlobs, { type: this.type }).arrayBuffer();

    await this.ctx.idb.put("recordings", {
      id: this.id,
      streamId: this.streamId,
      slice: this.localRecordedBlobs.length - 1,
      data: {
        storyId: this.storyId,
        streamId: this.streamId,
        sessionId: this.sessionId,
        recordingStartTime: this.recordingStartTime,
        recordingStopTime: this.recordingStopTime,
        localTimeOffset: this.localTimeOffset,
        recordedArrayBuffer: arrayBuffer,
        userId: this.userId,
        type: this.type,
      },
    });
  }
}

export class StoredLocalAudioStream extends LocalAudioStream {
  recordedArrayBuffer;

  constructor(ctx: LocalMediaStorageContext, id: string, recording: LocalDbRecordingsData, uploadCb: UploadProgressCB) {
    super(ctx, id, recording, uploadCb);
    this.recordedArrayBuffer = recording.recordedArrayBuffer;
  }

  getDataBlob(): Blob {
    return arrayBufferToBlob(this.recordedArrayBuffer, this.type || "audio/webm");
  }
}

type SyncRecordingsCB = (stream: LocalAudioStream) => void;
type UploadProgressCB = (stream: LocalAudioStream, event: { bytesUploaded: number; totalBytes: number }) => void;

const NoOp = () => {
  /* Default behavior: No action */
};

export class LocalMediaStorage {
  ctx = new LocalMediaStorageContext();
  _syncInProgress = false;
  pendingSyncCallback = NoOp as SyncRecordingsCB;
  preSyncCallback = NoOp as SyncRecordingsCB;
  postSyncCallback = NoOp as SyncRecordingsCB;
  uploadProgressCallback = NoOp as UploadProgressCB;

  async createStream({ streamId, storyId, sessionId, localTimeOffset, userId }: InitialLocalDbRecordingsData) {
    await this.ctx.ensureIDB();
    return new InMemoryLocalAudioStream(
      this.ctx,
      { streamId, storyId, sessionId, localTimeOffset, userId },
      this.uploadProgressCallback
    );
  }

  async getRecording() {
    await this.ctx.ensureIDB();
    const recordings = await this.ctx.idb.getAll("recordings", null, 1);
    return !recordings || recordings.length === 0 ? null : recordings[0];
  }

  async syncRecordings(immediately = false) {
    if (this._syncInProgress) return;

    this._syncInProgress = true;
    this.ctx.trace("LocalMediaStorage media sync check - STARTING...");
    await this.ctx.ensureIDB();

    for (;;) {
      // We are going to upload one file at a time until the DB is empty so
      // we are doing this the slow way without getting everything from DB and
      // hitting AWS all at once. The implementation here is a bit less than ideal
      // as it will stop if it finds a recording that was updated less than 60 sec
      // ago even though there might be other ones, but it's a simple stop-gap for
      // now as we retrieve one record at a time from IDB and if we don't delete the
      // record, we can't retrieve the next one
      const recording = await this.getRecording();
      if (recording == null) {
        break;
      }
      const stream = new StoredLocalAudioStream(this.ctx, recording.id, recording.data, this.uploadProgressCallback);
      if (!immediately && stream.recordingStopTime > new Date().getTime() - 60000) {
        this.pendingSyncCallback(stream);
        break;
      }

      this.preSyncCallback(stream);
      await stream.finish();
      await stream.delete();
      this.postSyncCallback(stream);
    }
    this.ctx.trace("LocalMediaStorage media sync check - FINISHED");
    this._syncInProgress = false;
  }
}

const _LMS = new LocalMediaStorage();

export default _LMS;
